/**
 * ISO 8601 week helpers.
 *
 * ISO weeks are numbered so that week 1 is the week containing January 4th
 * (equivalently, the week containing the first Thursday of the year). Weeks
 * start on Monday. Week strings use the format "YYYY-WNN" (e.g. "2025-W32"),
 * where YYYY is the ISO week-numbering year (which can differ from the calendar
 * year near year boundaries).
 *
 * All date math is done in UTC to avoid off-by-one errors caused by local
 * timezone offsets / DST.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Returns the ISO weekday for a date, where Monday = 1 … Sunday = 7.
 */
function isoDayOfWeek(date: Date): number {
  const day = date.getUTCDay(); // 0 (Sun) … 6 (Sat)
  return day === 0 ? 7 : day;
}

/**
 * Formats an ISO week-numbering year + week number into "YYYY-WNN".
 */
function formatIsoWeek(year: number, week: number): string {
  return `${String(year).padStart(4, "0")}-W${String(week).padStart(2, "0")}`;
}

/**
 * Parses a "YYYY-WNN" string into its numeric year/week components.
 * Throws if the string is malformed.
 */
function parseIsoWeek(isoWeek: string): { year: number; week: number } {
  const match = /^(\d{4})-W(\d{2})$/.exec(isoWeek);
  if (match === null) {
    throw new Error(`Invalid ISO week string: "${isoWeek}"`);
  }
  const year = Number(match[1]);
  const week = Number(match[2]);
  if (week < 1 || week > 53) {
    throw new Error(`Invalid ISO week number: "${isoWeek}"`);
  }
  return { year, week };
}

/**
 * Returns the UTC `Date` of the Monday that begins the given ISO week.
 *
 * Algorithm: January 4th is always in ISO week 1. The Monday of week 1 is the
 * Monday on or before Jan 4. From there, add (week - 1) * 7 days.
 */
export function isoWeekToMonday(isoWeek: string): Date {
  const { year, week } = parseIsoWeek(isoWeek);

  // Jan 4th of the ISO year is always within week 1.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  // Monday of week 1 = Jan 4 minus (its ISO weekday - 1) days.
  const week1Monday = new Date(
    jan4.getTime() - (isoDayOfWeek(jan4) - 1) * MS_PER_DAY,
  );
  return new Date(week1Monday.getTime() + (week - 1) * 7 * MS_PER_DAY);
}

/**
 * Derives the "YYYY-WNN" ISO week string that contains the given date.
 */
export function dateToIsoWeek(date: Date): string {
  // Work from a UTC-normalized copy at midnight.
  const target = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );

  // Shift to the Thursday of the current ISO week — the Thursday determines
  // the ISO week-numbering year.
  const dayNum = isoDayOfWeek(target);
  target.setUTCDate(target.getUTCDate() + (4 - dayNum));

  const isoYear = target.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil(
    ((target.getTime() - yearStart.getTime()) / MS_PER_DAY + 1) / 7,
  );

  return formatIsoWeek(isoYear, week);
}

/**
 * Returns the current ISO week as "YYYY-WNN".
 */
export function getCurrentIsoWeek(): string {
  return dateToIsoWeek(new Date());
}

/**
 * Shifts an ISO week string by `delta` weeks (each week = exactly ±7 days)
 * and returns the resulting "YYYY-WNN" string.
 */
export function addWeeks(isoWeek: string, delta: number): string {
  const monday = isoWeekToMonday(isoWeek);
  const shifted = new Date(monday.getTime() + delta * 7 * MS_PER_DAY);
  return dateToIsoWeek(shifted);
}
